<script setup lang="ts">
import { UiButton } from '@arpm/ui';
import { ArrowUpRight, LogOut, ShieldCheck, Ticket } from 'lucide-vue-next';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import { useAuthStore } from '@/stores/auth.js';

const auth = useAuthStore();
const router = useRouter();
const { t } = useI18n();

const lkUrl = import.meta.env.VITE_LK_URL ?? '';
const initials = computed(() =>
  (auth.user?.displayName ?? '')
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join(''),
);

function goHome() {
  void router.push({ name: 'events' });
}
async function logout() {
  await auth.logout();
}
</script>

<template>
  <div class="shell">
    <header class="shell__header">
      <button class="brand" type="button" @click="goHome">
        <span class="brand__mark"><Ticket :size="18" /></span>
        <span class="brand__title">{{ t('app.name') }}</span>
      </button>

      <div class="shell__spacer" />

      <button
        v-if="auth.isPlatformAdmin"
        class="lk"
        type="button"
        title="Администраторы сервиса"
        @click="router.push({ name: 'service-admins' })"
      >
        <ShieldCheck :size="15" />
        Администраторы
      </button>

      <a v-if="lkUrl" class="lk" :href="lkUrl">
        {{ t('shell.toLk') }}
        <ArrowUpRight :size="15" />
      </a>

      <div v-if="auth.user" class="user">
        <span class="user__avatar">{{ initials }}</span>
        <span class="user__name">{{ auth.user.displayName }}</span>
      </div>

      <UiButton variant="ghost" @click="logout">
        <LogOut :size="16" />
        {{ t('shell.logout') }}
      </UiButton>
    </header>

    <main class="shell__main">
      <slot />
    </main>
  </div>
</template>

<style scoped>
.shell {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--color-bg-surface);
}
.shell__header {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: 0 var(--space-5);
  height: 60px;
  background: var(--color-bg);
  border-bottom: 1px solid var(--color-border);
  position: sticky;
  top: 0;
  z-index: var(--z-dropdown);
}
.brand {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  font: inherit;
}
.brand__mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: var(--radius-md);
  background: var(--color-primary-soft);
  color: var(--color-primary);
}
.brand__title {
  font-weight: var(--font-weight-bold);
  color: var(--color-text);
}
.shell__spacer {
  flex: 1;
}
button.lk {
  background: none;
  border: none;
  cursor: pointer;
  font: inherit;
}
.lk {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: var(--font-size-sm);
  color: var(--color-primary);
}
.lk:hover {
  text-decoration: none;
  color: var(--color-primary-hover);
}
.user {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
}
.user__avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: var(--radius-full);
  background: var(--color-primary-soft);
  color: var(--color-primary);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  flex: none;
}
.user__name {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
}
.shell__main {
  flex: 1;
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
  padding: var(--space-6) var(--space-5);
}
@media (max-width: 640px) {
  .user__name,
  button.lk {
  background: none;
  border: none;
  cursor: pointer;
  font: inherit;
}
.lk {
    display: none;
  }
}
</style>
